import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Minus, Plus, Cake, Heart, Briefcase, Plane, ThumbsUp, Gift, Check, CheckCircle2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import BookingHeader from '@features/booking/components/BookingHeader';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { useTenant } from '@core/tenant';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { getGiftCardExperiences, buyGiftCard } from '../api/giftCardApi';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { capturePaymentError } from '@core/sentry';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { formatAmount } from '@shared/utils/formatAmount';
import { useMercadoPagoSDK } from '@features/booking/hooks/useMercadoPagoSDK';
import { useStripeV2, getStripeErrorMessage } from '@features/booking/hooks/useStripeV2';
import type { StripeTokenError } from '@features/booking/hooks/useStripeV2';
import { encryptCardData } from '@features/booking/utils/encryptCardData';
import { env } from '@core/config/env';
import { Private } from '@shared/components/common/Private';
import type { GiftCardExperience, GiftCardOccasion, PurchaseStep, BuyGiftCardPayload } from '../types';

// ── Occasion config ──────────────────────────────────────────────────

const OCCASIONS: { key: GiftCardOccasion; icon: typeof Cake }[] = [
  { key: 'birthday', icon: Cake },
  { key: 'anniversary', icon: Heart },
  { key: 'business', icon: Briefcase },
  { key: 'trip', icon: Plane },
  { key: 'thankyou', icon: ThumbsUp },
  { key: 'gift', icon: Gift },
];

// ── Helper: format card inputs ───────────────────────────────────────

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length >= 3) {
    return digits.slice(0, 2) + '/' + digits.slice(2, 4);
  }
  return digits;
}

// ── Stripe Gift Card Form (Stripe v2) ────────────────────────────────

interface StripeGiftCardFormProps {
  publishableKey: string;
  amount: number;
  serviceFee: number;
  currency: string;
  expirationDays: number;
  serviceChargeWarning: boolean;
  allowGiftCardServiceFee: boolean;
  onSuccess: (cardToken: string, paymentMethodId: string, extras?: { cardTokenStripe: string }) => Promise<void>;
  onBack: () => void;
}

function StripeGiftCardForm({
  publishableKey,
  amount,
  serviceFee,
  currency,
  expirationDays,
  serviceChargeWarning,
  allowGiftCardServiceFee,
  onSuccess,
  onBack,
}: StripeGiftCardFormProps) {
  const { t, i18n } = useTranslation('gift-cards');
  const stripeV2 = useStripeV2(publishableKey);
  const stripeServiceFee = useStripeV2(allowGiftCardServiceFee ? env.stripeFallbackKey : '');

  const stripeFormRef = useRef<HTMLFormElement>(null);
  const serviceFeeFormRef = useRef<HTMLFormElement>(null);

  const [holderName, setHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  const [allAccepted, setAllAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalAmount = amount + serviceFee;

  const syncHiddenForm = useCallback(() => {
    const digits = cardNumber.replace(/\D/g, '');
    const parts = expiry.split('/');
    const month = parts[0] || '';
    const year = parts[1] || '';

    if (stripeFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = stripeFormRef.current!.querySelector(`#sv2-gc-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('name', holderName);
      setVal('number', digits);
      setVal('exp-month', month);
      setVal('exp-year', year);
      setVal('cvc', cvc);
    }
  }, [holderName, cardNumber, expiry, cvc]);

  const handleSubmit = async () => {
    if (!stripeV2.isReady || !allAccepted) return;
    setIsProcessing(true);
    setError(null);

    const digits = cardNumber.replace(/\D/g, '');
    if (!holderName.trim()) { setError(t('GIFTCARD_PAYMENT_HOLDER_NAME_REQUIRED')); setIsProcessing(false); return; }
    if (digits.length < 13 || digits.length > 19) { setError(t('GIFTCARD_PAYMENT_INVALID_CARD_NUMBER')); setIsProcessing(false); return; }
    if (!/^(0[1-9]|1[0-2])\/(\d{2})$/.test(expiry)) { setError(t('GIFTCARD_PAYMENT_INVALID_EXPIRY')); setIsProcessing(false); return; }
    if (cvc.length < 3 || cvc.length > 4) { setError(t('GIFTCARD_PAYMENT_INVALID_CVC')); setIsProcessing(false); return; }

    syncHiddenForm();

    // Also sync service fee hidden form (same card data, different Stripe key)
    if (serviceFeeFormRef.current) {
      const digits = cardNumber.replace(/\D/g, '');
      const parts = expiry.split('/');
      const setVal = (id: string, val: string) => {
        const el = serviceFeeFormRef.current!.querySelector(`#sv2-sf-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('name', holderName);
      setVal('number', digits);
      setVal('exp-month', parts[0] || '');
      setVal('exp-year', parts[1] || '');
      setVal('cvc', cvc);
    }

    try {
      const response = await stripeV2.createToken(stripeFormRef.current!);
      if (!response.id) {
        setError(t('GIFTCARD_PAYMENT_CARD_ERROR'));
        setIsProcessing(false);
        return;
      }

      // Create service fee token with fallback Stripe key (best-effort)
      let serviceFeeToken = '';
      if (allowGiftCardServiceFee && stripeServiceFee.isReady && serviceFeeFormRef.current) {
        try {
          const sfResponse = await stripeServiceFee.createToken(serviceFeeFormRef.current!);
          serviceFeeToken = sfResponse.id || '';
        } catch {
          // Service fee token is best-effort
        }
      }

      await onSuccess(
        response.id,
        response.card?.brand || '',
        serviceFeeToken ? { cardTokenStripe: serviceFeeToken } : undefined,
      );
    } catch (err) {
      const e = err as StripeTokenError;
      const msg = e.code
        ? getStripeErrorMessage({ code: e.code, message: e.message || t('GIFTCARD_PAYMENT_CARD_ERROR') }, t)
        : t('GIFTCARD_PAYMENT_CARD_ERROR');
      setError(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_STRING_12')}</h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_PAYMENT_SUBTITLE')}</p>

      <div className="w-full max-w-sm px-4 space-y-6">
        {!stripeV2.isReady && (
          <div className="text-center text-foreground/50 text-sm py-4">
            {stripeV2.error || t('GIFTCARD_PAYMENT_LOADING')}
          </div>
        )}

        {stripeV2.isReady && (
          // GiftCard payment uses Stripe v1's legacy tokenization (NOT
          // iframe-isolated Stripe Elements). PAN / CVC / expiry live in
          // React state + plain <input>. Without this <Private> wrapper a
          // session-replay tool would record card data — PCI-DSS violation.
          // Identical risk to MercadoPagoPaymentForm.
          <Private className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_HOLDER_NAME')}</label>
              <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_CARD_NUMBER')}</label>
              <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} placeholder="4242 4242 4242 4242" maxLength={23} inputMode="numeric" />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_EXPIRY')}</label>
                <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={expiry} onChange={(e) => setExpiry(formatExpiry(e.target.value))} placeholder="MM/YY" maxLength={5} inputMode="numeric" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_CVC')}</label>
                <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={cvc} onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="123" maxLength={4} inputMode="numeric" />
              </div>
            </div>
          </Private>
        )}

        {/* Hidden Stripe v2 form (main payment) — data-private belt-and-
            suspenders even though display:none is usually skipped. */}
        <form ref={stripeFormRef} name="stripeGCForm" data-private style={{ display: 'none' }}>
          <input id="sv2-gc-name" data-stripe="name" type="hidden" />
          <input id="sv2-gc-number" data-stripe="number" type="hidden" />
          <input id="sv2-gc-exp-month" data-stripe="exp-month" type="hidden" />
          <input id="sv2-gc-exp-year" data-stripe="exp-year" type="hidden" />
          <input id="sv2-gc-cvc" data-stripe="cvc" type="hidden" />
        </form>

        {/* Hidden Stripe v2 form for service fee (uses fallback key) */}
        {allowGiftCardServiceFee && (
          <form ref={serviceFeeFormRef} name="stripeSFForm" data-private style={{ display: 'none' }}>
            <input id="sv2-sf-name" data-stripe="name" type="hidden" />
            <input id="sv2-sf-number" data-stripe="number" type="hidden" />
            <input id="sv2-sf-exp-month" data-stripe="exp-month" type="hidden" />
            <input id="sv2-sf-exp-year" data-stripe="exp-year" type="hidden" />
            <input id="sv2-sf-cvc" data-stripe="cvc" type="hidden" />
          </form>
        )}

        {/* Amount summary */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-foreground/70">
            <span>{t('BUY_A_GIFTCARD_STRING_13')}</span>
            <span>{currency} {formatAmount(amount, i18n.language)}</span>
          </div>
          {serviceFee > 0 && (
            <div className="flex justify-between text-sm text-foreground/70">
              <span>{t('GIFTCARD_PAYMENT_SERVICE_FEE')}</span>
              <span>{currency} {formatAmount(serviceFee, i18n.language)}</span>
            </div>
          )}
          <div className="flex justify-between font-medium text-base border-t border-border translucent:border-surface-border pt-2">
            <span>{t('GIFTCARD_PAYMENT_TOTAL')}</span>
            <span>{currency} {formatAmount(totalAmount, i18n.language)}</span>
          </div>
        </div>

        {/* Acceptances */}
        <GiftCardAcceptances
          expirationDays={expirationDays}
          serviceChargeWarning={serviceChargeWarning}
          hasServiceFee={serviceFee > 0}
          onAllAcceptedChange={setAllAccepted}
        />

        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}
      </div>

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!stripeV2.isReady || !allAccepted || isProcessing}
        >
          {isProcessing ? t('GIFTCARD_PAYMENT_PROCESSING') : t('BUY_A_GIFTCARD_STRING_11')}
        </Button>
      </div>

      <footer className="border-t border-border translucent:border-surface-border w-full mt-8">
        <div className="flex items-center px-6 py-4">
          <button
            aria-label="Go back"
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={onBack}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </footer>
    </>
  );
}

// ── MercadoPago inner form ───────────────────────────────────────────

interface MPGiftCardFormProps {
  amount: number;
  serviceFee: number;
  currency: string;
  expirationDays: number;
  serviceChargeWarning: boolean;
  onSuccess: (cardToken: string, paymentMethodId: string, extras?: { cardTokenStripe: string; temp1: string; temp3: string; temp4: string; temp6: string }) => Promise<void>;
  onBack: () => void;
}

function MPGiftCardForm({
  amount,
  serviceFee,
  currency,
  expirationDays,
  serviceChargeWarning,
  onSuccess,
  onBack,
}: MPGiftCardFormProps) {
  // 'booking' ns included for the shared PAYMENT_SDK_LOAD_ERROR copy —
  // mpSDK.error is a raw English diagnostic, never user-facing text.
  const { t, i18n } = useTranslation(['gift-cards', 'booking']);
  const mpSDK = useMercadoPagoSDK();
  const stripeV2 = useStripeV2(env.stripeFallbackKey);

  const stripeFormRef = useRef<HTMLFormElement>(null);

  const [holderName, setHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');

  const [allAccepted, setAllAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalAmount = amount + serviceFee;
  // Only gate on MercadoPago readiness. Stripe v2 is best-effort for dual-tokenization:
  // if it fails, the backend accepts cardTokenStripe='' and handles service fees without it.
  const sdksReady = mpSDK.isReady;

  const docNumberRef = useRef(
    generateRandomDocumentId().toString()
  );

  // Depend on the derived `bin` — not the full cardNumber — so the lookup
  // doesn't re-fire on every keystroke past digit 6.
  const bin = cardNumber.replace(/\D/g, '').slice(0, 6);
  useEffect(() => {
    if (bin.length >= 6 && mpSDK.isReady) {
      mpSDK.getPaymentMethod(bin).then(setPaymentMethodId).catch(() => {});
    }
  }, [bin, mpSDK.isReady, mpSDK.getPaymentMethod]);

  // MP tokenizes from React state directly (SDK v2 Core Methods, issue
  // #354); only Stripe v2's legacy form-reading API still needs a hidden
  // form synced before tokenization.
  const syncStripeHiddenForm = useCallback(() => {
    const digits = cardNumber.replace(/\D/g, '');
    const parts = expiry.split('/');
    const month = parts[0] || '';
    const year = parts[1] || '';

    if (stripeFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = stripeFormRef.current!.querySelector(`#sv2-gc-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('name', holderName);
      setVal('number', digits);
      setVal('exp-month', month);
      setVal('exp-year', year);
      setVal('cvc', cvc);
    }
  }, [holderName, cardNumber, expiry, cvc]);

  const handleSubmit = async () => {
    if (!sdksReady || !allAccepted) return;
    setIsProcessing(true);
    setError(null);

    const digits = cardNumber.replace(/\D/g, '');
    if (!holderName.trim()) { setError(t('GIFTCARD_PAYMENT_HOLDER_NAME_REQUIRED')); setIsProcessing(false); return; }
    if (digits.length < 13 || digits.length > 19) { setError(t('GIFTCARD_PAYMENT_INVALID_CARD_NUMBER')); setIsProcessing(false); return; }
    if (!/^(0[1-9]|1[0-2])\/(\d{2})$/.test(expiry)) { setError(t('GIFTCARD_PAYMENT_INVALID_EXPIRY')); setIsProcessing(false); return; }
    if (cvc.length < 3 || cvc.length > 4) { setError(t('GIFTCARD_PAYMENT_INVALID_CVC')); setIsProcessing(false); return; }

    syncStripeHiddenForm();

    try {
      // Stripe v2 token best-effort
      let cardTokenStripe = '';
      try {
        const stripeResponse = await stripeV2.createToken(stripeFormRef.current!);
        cardTokenStripe = stripeResponse.id || '';
      } catch {
        // Stripe v2 token is best-effort for MercadoPago flow
      }

      const parts = expiry.split('/');
      const mpToken = await mpSDK.createToken({
        holderName,
        cardNumber: digits,
        securityCode: cvc,
        expirationMonth: parts[0],
        expirationYear: parts[1],
        docNumber: docNumberRef.current,
      });

      const encrypted = encryptCardData({
        number: digits,
        expiryMonth: parts[0],
        expiryYear: parts[1],
        cvc,
        holderName,
      });

      await onSuccess(mpToken, paymentMethodId, {
        cardTokenStripe,
        temp1: encrypted.temp1,
        temp3: encrypted.temp3,
        temp4: encrypted.temp4,
        temp6: encrypted.temp6,
      });
    } catch (err) {
      const e = err as StripeTokenError;
      const msg = e.code
        ? getStripeErrorMessage({ code: e.code, message: e.message || t('GIFTCARD_PAYMENT_CARD_ERROR') }, t)
        : t('GIFTCARD_PAYMENT_CARD_ERROR');
      setError(msg);
      // Without this, MP tokenization/purchase failures on the gift-card
      // path never reach Sentry and the cause codes evaporate — the other
      // two MP consumers already capture.
      capturePaymentError(err, 'mercadopago', {
        source: 'gift-card-purchase',
        paymentMethodId,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_STRING_12')}</h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_PAYMENT_SUBTITLE')}</p>

      <div className="w-full max-w-sm px-4 space-y-6">
        {!sdksReady && (
          <div className="text-center text-foreground/50 text-sm py-4">
            {mpSDK.error ? t('booking:PAYMENT_SDK_LOAD_ERROR') : t('GIFTCARD_PAYMENT_LOADING')}
          </div>
        )}

        {sdksReady && (
          // MP gift-card path: same PCI-DSS risk as MercadoPagoPaymentForm.
          // PAN / CVC / expiry live in React state + plain <input>.
          <Private className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_HOLDER_NAME')}</label>
              <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="John Doe" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_CARD_NUMBER')}</label>
              <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} placeholder="4242 4242 4242 4242" maxLength={23} inputMode="numeric" />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_EXPIRY')}</label>
                <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={expiry} onChange={(e) => setExpiry(formatExpiry(e.target.value))} placeholder="MM/YY" maxLength={5} inputMode="numeric" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PAYMENT_CVC')}</label>
                <input type="text" className="w-full border border-border translucent:border-surface-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand" value={cvc} onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="123" maxLength={4} inputMode="numeric" />
              </div>
            </div>
          </Private>
        )}

        {/* Hidden Stripe v2 form — data-private belt-and-suspenders. (MP no
            longer needs a hidden form: SDK v2 tokenizes from the values
            passed to createToken directly — issue #354.) */}
        <form ref={stripeFormRef} name="stripeGCForm" data-private style={{ display: 'none' }}>
          <input id="sv2-gc-name" data-stripe="name" type="hidden" />
          <input id="sv2-gc-number" data-stripe="number" type="hidden" />
          <input id="sv2-gc-exp-month" data-stripe="exp-month" type="hidden" />
          <input id="sv2-gc-exp-year" data-stripe="exp-year" type="hidden" />
          <input id="sv2-gc-cvc" data-stripe="cvc" type="hidden" />
        </form>

        {/* Amount summary */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-foreground/70">
            <span>{t('BUY_A_GIFTCARD_STRING_13')}</span>
            <span>{currency} {formatAmount(amount, i18n.language)}</span>
          </div>
          {serviceFee > 0 && (
            <div className="flex justify-between text-sm text-foreground/70">
              <span>{t('GIFTCARD_PAYMENT_SERVICE_FEE')}</span>
              <span>{currency} {formatAmount(serviceFee, i18n.language)}</span>
            </div>
          )}
          <div className="flex justify-between font-medium text-base border-t border-border translucent:border-surface-border pt-2">
            <span>{t('GIFTCARD_PAYMENT_TOTAL')}</span>
            <span>{currency} {formatAmount(totalAmount, i18n.language)}</span>
          </div>
        </div>

        {/* Acceptances */}
        <GiftCardAcceptances
          expirationDays={expirationDays}
          serviceChargeWarning={serviceChargeWarning}
          hasServiceFee={serviceFee > 0}
          onAllAcceptedChange={setAllAccepted}
        />

        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}
      </div>

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!sdksReady || !allAccepted || isProcessing}
        >
          {isProcessing ? t('GIFTCARD_PAYMENT_PROCESSING') : t('BUY_A_GIFTCARD_STRING_11')}
        </Button>
      </div>

      <footer className="border-t border-border translucent:border-surface-border w-full mt-8">
        <div className="flex items-center px-6 py-4">
          <button
            aria-label="Go back"
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={onBack}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </footer>
    </>
  );
}

// ── Gift Card Acceptances ────────────────────────────────────────────

interface GiftCardAcceptancesProps {
  expirationDays: number;
  serviceChargeWarning: boolean;
  hasServiceFee: boolean;
  onAllAcceptedChange: (all: boolean) => void;
}

function GiftCardAcceptances({ expirationDays, serviceChargeWarning, hasServiceFee, onAllAcceptedChange }: GiftCardAcceptancesProps) {
  const { t } = useTranslation('gift-cards');

  const items: { key: string; label: string; show: boolean }[] = [
    { key: 'validity', label: t('BUY_A_GIFTCARD_STRING_30', { days: expirationDays }), show: expirationDays > 0 },
    { key: 'serviceCharge', label: t('GIFTCARD_PAYMENT_ACCEPT_SERVICE_CHARGE'), show: serviceChargeWarning },
    { key: 'bookTable', label: t('GIFTCARD_PAYMENT_ACCEPT_BOOK_TABLE'), show: true },
    { key: 'nonRefundable', label: t('GIFTCARD_PAYMENT_ACCEPT_NON_REFUNDABLE'), show: true },
    { key: 'serviceFee', label: t('GIFTCARD_PAYMENT_ACCEPT_SERVICE_FEE'), show: hasServiceFee },
  ];

  const visibleItems = items.filter(i => i.show);
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});

  const toggle = (key: string) => {
    setAccepted(prev => {
      const next = { ...prev, [key]: !prev[key] };
      const allDone = visibleItems.every(i => next[i.key]);
      onAllAcceptedChange(allDone);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {visibleItems.map(item => (
        <div key={item.key} className="flex justify-between items-start gap-4">
          <p className="font-medium text-sm text-foreground flex-1">{item.label}</p>
          <button
            type="button"
            className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors ${
              accepted[item.key] ? 'bg-brand text-brand-foreground' : 'bg-card border border-border translucent:border-surface-border text-foreground/70'
            }`}
            onClick={() => toggle(item.key)}
          >
            {accepted[item.key] ? <Check className="h-5 w-5" /> : 'ACCEPT'}
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Main Purchase Page ───────────────────────────────────────────────

const GiftCardPurchasePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('gift-cards');
  const { restaurant } = useTenant();
  const restaurantData = useRestaurant();
  const { trackEvent } = useAnalytics();
  const lang = i18n.language;

  const [step, setStep] = useState<PurchaseStep>('select');

  // Selection state
  const [selectedExperience, setSelectedExperience] = useState<GiftCardExperience | null>(null);
  const [partySize, setPartySize] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);

  // Personalization state
  const [emailFrom, setEmailFrom] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailToConfirm, setEmailToConfirm] = useState('');
  const [occasion, setOccasion] = useState<GiftCardOccasion | null>(null);
  const [note, setNote] = useState('');
  const [personalizeError, setPersonalizeError] = useState<string | null>(null);

  // Fetch experiences — lang is in the queryKey so changing language refetches.
  const { data: experiences, isLoading: experiencesLoading } = useQuery({
    queryKey: ['giftCardExperiences', restaurant?.id, lang],
    queryFn: () => getGiftCardExperiences(lang, restaurant!.id),
    enabled: !!restaurant?.id,
  });

  // Computed amount
  const amount = useMemo(() => {
    if (!selectedExperience) return 0;
    if (selectedExperience.experienceType === 'menu') {
      return (selectedExperience.prices[0] || 0) * partySize;
    }
    return selectedAmount || 0;
  }, [selectedExperience, partySize, selectedAmount]);

  // Service fee
  const serviceFeePercent = restaurantData.checkoutProcessingFeePercent || 0;
  const serviceFee = serviceFeePercent > 0 ? Math.round(amount * serviceFeePercent / 100) : 0;

  // Max party size
  const maxPartySize = useMemo(() => {
    if (restaurantData.allowedPartySizes && restaurantData.allowedPartySizes.length > 0) {
      return Math.max(...restaurantData.allowedPartySizes);
    }
    return 20;
  }, [restaurantData.allowedPartySizes]);

  const isMercadoPago = restaurantData.paymentProcessor === 'mercadopago';

  // ── Validation helpers ──

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const canContinueSelect = selectedExperience && amount > 0;

  const validatePersonalize = (): boolean => {
    if (!emailFrom || !isValidEmail(emailFrom)) {
      setPersonalizeError(t('GIFTCARD_PERSONALIZE_INVALID_EMAIL_FROM'));
      return false;
    }
    if (!emailTo || !isValidEmail(emailTo)) {
      setPersonalizeError(t('GIFTCARD_PERSONALIZE_INVALID_EMAIL_TO'));
      return false;
    }
    if (emailTo !== emailToConfirm) {
      setPersonalizeError(t('GIFTCARD_PERSONALIZE_EMAIL_MISMATCH'));
      return false;
    }
    if (!occasion) {
      setPersonalizeError(t('GIFTCARD_PERSONALIZE_OCCASION_REQUIRED'));
      return false;
    }
    setPersonalizeError(null);
    return true;
  };

  // ── Payment success handler ──

  const handlePaymentSuccess = async (
    cardToken: string,
    paymentMethodId: string,
    extras?: { cardTokenStripe: string; temp1?: string; temp3?: string; temp4?: string; temp6?: string },
  ) => {
    if (!selectedExperience) return;

    const payload: BuyGiftCardPayload = {
      data: {
        experience: selectedExperience.id,
        partySize: selectedExperience.experienceType === 'menu' ? partySize : 1,
        emailFrom,
        emailTo,
        note,
        occasion: occasion || '',
        amountChosen: amount,
        cardToken,
        paymentMethodId,
        ...(extras?.cardTokenStripe ? { cardTokenServiceFee: extras.cardTokenStripe } : {}),
      },
    };

    await buyGiftCard(lang, payload);
    trackEvent({
      name: ANALYTICS_EVENTS.GIFT_CARD_PURCHASE,
      properties: {
        amount,
        currency: restaurantData.currency,
        experience_name: selectedExperience.name,
        experience_type: selectedExperience.experienceType,
      },
    });
    setStep('confirmation');
  };

  // ── Back handler ──

  const handleBack = () => {
    if (step === 'select') {
      navigate('/');
    } else if (step === 'personalize') {
      setStep('select');
    } else if (step === 'payment') {
      setStep('personalize');
    } else {
      navigate('/');
    }
  };

  // ── Step renderers ──

  const renderSelectStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_STRING_1')}</h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">{t('BUY_A_GIFTCARD_STRING_2')}</p>

      <div className="w-full max-w-sm px-4 space-y-4">
        {experiencesLoading && (
          <div className="text-center text-foreground/40 text-sm py-8">{t('BUY_A_GIFTCARD_SELECT_LOADING')}</div>
        )}

        {experiences?.map(exp => (
          <button
            key={exp.id}
            type="button"
            className={`w-full text-left rounded-xl border-2 p-4 transition-all bg-card ${
              selectedExperience?.id === exp.id
                ? 'border-brand bg-brand/5'
                : 'border-border translucent:border-surface-border hover:border-brand/50'
            }`}
            onClick={() => {
              setSelectedExperience(exp);
              setSelectedAmount(null);
              setPartySize(1);
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <p className="font-medium text-foreground">{exp.name}</p>
                {exp.description && (
                  <div
                    className="text-sm text-foreground/50 mt-1 rich-html-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(exp.description) }}
                  />
                )}
                {exp.expirationDays > 0 && (
                  <p className="text-xs text-foreground/40 mt-2">
                    {t('BUY_A_GIFTCARD_STRING_30', { days: exp.expirationDays })}
                  </p>
                )}
              </div>
              {selectedExperience?.id === exp.id && (
                <CheckCircle2 className="text-brand flex-shrink-0 ml-2" size={20} />
              )}
            </div>
          </button>
        ))}

        {/* Menu type: party size picker */}
        {selectedExperience?.experienceType === 'menu' && (
          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium text-foreground/70">{t('BUY_A_GIFTCARD_SELECT_PARTY_SIZE')}</p>
            <div className="flex items-center justify-center gap-6">
              <button
                type="button"
                className="w-10 h-10 rounded-full bg-card border border-border translucent:border-surface-border flex items-center justify-center hover:border-brand disabled:opacity-30"
                onClick={() => setPartySize(p => Math.max(1, p - 1))}
                disabled={partySize <= 1}
              >
                <Minus size={16} />
              </button>
              <span className="text-2xl font-semibold w-12 text-center">{partySize}</span>
              <button
                type="button"
                className="w-10 h-10 rounded-full bg-card border border-border translucent:border-surface-border flex items-center justify-center hover:border-brand disabled:opacity-30"
                onClick={() => setPartySize(p => Math.min(maxPartySize, p + 1))}
                disabled={partySize >= maxPartySize}
              >
                <Plus size={16} />
              </button>
            </div>
            <p className="text-center text-sm text-foreground/50">
              {restaurantData.currency} {formatAmount((selectedExperience.prices[0] || 0) * partySize, lang)}
            </p>
          </div>
        )}

        {/* Money type: amount buttons */}
        {selectedExperience?.experienceType === 'money' && (
          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium text-foreground/70">{t('BUY_A_GIFTCARD_SELECT_CHOOSE_AMOUNT')}</p>
            <div className="grid grid-cols-2 gap-3">
              {selectedExperience.prices.map(price => (
                <button
                  key={price}
                  type="button"
                  className={`py-3 rounded-xl border-2 font-medium transition-all bg-card ${
                    selectedAmount === price
                      ? 'border-brand bg-brand/5 text-brand'
                      : 'border-border translucent:border-surface-border text-foreground/70 hover:border-brand/50'
                  }`}
                  onClick={() => setSelectedAmount(price)}
                >
                  {restaurantData.currency} {formatAmount(price, lang)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={() => setStep('personalize')}
          disabled={!canContinueSelect}
        >
          {t('BUY_A_GIFTCARD_SELECT_CONTINUE')}
        </Button>
      </div>

      <footer className="border-t border-border translucent:border-surface-border w-full mt-8">
        <div className="flex items-center px-6 py-4">
          <button
            aria-label="Go back"
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={() => navigate('/')}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </footer>
    </>
  );

  const renderPersonalizeStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_STRING_5')}</h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_PERSONALIZE_SUBTITLE')}</p>

      <div className="w-full max-w-sm px-4 space-y-4">
        {/* Gift-card sender/recipient emails + free-form note are PII that
            session-replay tools must not capture. Wrap all three email
            inputs (lines below) and the note textarea (further down) under
            data-private. The occasion picker is wrapped incidentally —
            harmless, it's just icons. */}
        <Private className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground/70 mb-1">{t('BUY_A_GIFTCARD_STRING_8')}</label>
            <Input
              type="email"
              value={emailFrom}
              onChange={e => { setEmailFrom(e.target.value); setPersonalizeError(null); }}
              placeholder={t('GIFTCARD_PERSONALIZE_EMAIL_FROM_PLACEHOLDER')}
              className="py-5"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/70 mb-1">{t('BUY_A_GIFTCARD_STRING_6')}</label>
            <Input
              type="email"
              value={emailTo}
              onChange={e => { setEmailTo(e.target.value); setPersonalizeError(null); }}
              placeholder={t('GIFTCARD_PERSONALIZE_EMAIL_TO_PLACEHOLDER')}
              className="py-5"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/70 mb-1">{t('GIFTCARD_PERSONALIZE_CONFIRM_EMAIL')}</label>
            <Input
              type="email"
              value={emailToConfirm}
              onChange={e => { setEmailToConfirm(e.target.value); setPersonalizeError(null); }}
              placeholder={t('GIFTCARD_PERSONALIZE_CONFIRM_EMAIL_PLACEHOLDER')}
              className="py-5"
            />
          </div>
        </Private>

        {/* Occasion picker */}
        <div>
          <p className="text-sm font-medium text-foreground/70 mb-3">{t('BUY_A_GIFTCARD_STRING_23')}</p>
          <div className="grid grid-cols-3 gap-3">
            {OCCASIONS.map(({ key, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={`flex flex-col items-center justify-center gap-1.5 aspect-square rounded-xl border-2 transition-all text-xs leading-tight text-center bg-card ${
                  occasion === key
                    ? 'border-brand bg-brand/5 text-brand'
                    : 'border-border translucent:border-surface-border text-foreground/70 hover:border-brand/50'
                }`}
                onClick={() => { setOccasion(key); setPersonalizeError(null); }}
              >
                <Icon size={22} />
                <span className="px-1">{t(`GIFTCARD_OCCASION_${key.toUpperCase()}`)}</span>
              </button>
            ))}
          </div>
        </div>

        <Private>
          <div>
            <label className="block text-sm font-medium text-foreground/70 mb-1">{t('BUY_A_GIFTCARD_STRING_10')}</label>
            <textarea
              className="w-full rounded-xl border border-border translucent:border-surface-border p-3 text-foreground focus:border-brand focus:outline-none resize-none"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('GIFTCARD_PERSONALIZE_NOTE_PLACEHOLDER')}
              rows={3}
            />
          </div>
        </Private>

        {personalizeError && (
          <p className="text-red-500 text-sm text-center">{personalizeError}</p>
        )}
      </div>

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => {
            if (validatePersonalize()) setStep('payment');
          }}
        >
          {t('GIFTCARD_PERSONALIZE_CONTINUE')}
        </Button>
      </div>

      <footer className="border-t border-border translucent:border-surface-border w-full mt-8">
        <div className="flex items-center px-6 py-4">
          <button
            aria-label="Go back"
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={() => setStep('select')}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
      </footer>
    </>
  );

  const renderPaymentStep = () => {
    if (!selectedExperience) return null;

    const paymentProps = {
      amount,
      serviceFee,
      currency: restaurantData.currency,
      expirationDays: selectedExperience.expirationDays,
      serviceChargeWarning: selectedExperience.serviceChargeWarning,
      onBack: () => setStep('personalize'),
    };

    if (isMercadoPago) {
      return (
        <MPGiftCardForm
          {...paymentProps}
          onSuccess={handlePaymentSuccess}
        />
      );
    }

    if (!restaurantData.publicKeyPayment) {
      return (
        <div className="text-center text-red-500 py-8">
          Payment configuration missing. Please contact the restaurant.
        </div>
      );
    }

    return (
      <StripeGiftCardForm
        {...paymentProps}
        publishableKey={restaurantData.publicKeyPayment}
        allowGiftCardServiceFee={!!restaurantData.allowGiftCardServiceFee}
        onSuccess={handlePaymentSuccess}
      />
    );
  };

  const renderConfirmationStep = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="text-green-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">{t('BUY_A_GIFTCARD_STRING_15')}</h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">{t('GIFTCARD_CONFIRMATION_SUBTITLE')}</p>
      </div>

      <div className="w-full max-w-sm px-4 space-y-4">
        <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-foreground/50">{t('GIFTCARD_CONFIRMATION_EXPERIENCE')}</span>
            <span className="font-medium text-foreground">{selectedExperience?.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-foreground/50">{t('GIFTCARD_CONFIRMATION_RECIPIENT')}</span>
            <span className="font-medium text-foreground">
              <Private inline>{emailTo}</Private>
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-foreground/50">{t('GIFTCARD_CONFIRMATION_AMOUNT')}</span>
            <span className="font-medium text-foreground">{restaurantData.currency} {formatAmount(amount, lang)}</span>
          </div>
          {occasion && (
            <div className="flex justify-between text-sm">
              <span className="text-foreground/50">{t('GIFTCARD_CONFIRMATION_OCCASION')}</span>
              <span className="font-medium text-foreground">{t(`GIFTCARD_OCCASION_${occasion.toUpperCase()}`)}</span>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-foreground/50">{t('BUY_A_GIFTCARD_STRING_16')}</p>
      </div>

      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('GIFTCARD_CONFIRMATION_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {step === 'select' && renderSelectStep()}
          {step === 'personalize' && renderPersonalizeStep()}
          {step === 'payment' && renderPaymentStep()}
          {step === 'confirmation' && renderConfirmationStep()}
        </main>

        {step === 'confirmation' && <BookingFooter />}
    </BookingChrome>
  );
};

export default GiftCardPurchasePage;
